import { optimizationApi } from '@/services/api';
import { apiClient } from '@/services/api/client';
import type { Campaign } from '@/types/campaign';
import type { AppCampaignConfig } from '@/types/api';
import type { QueryClient } from '@tanstack/react-query';
import { normalizeUrl } from './urlUtils';
import { SESSION_KEYS } from '@/config/constants';

// Internal type for items that may have URL lists (used for type assertions)
interface UrlItem {
  final_urls?: Array<{ url: string; flouri_scraping_enabled?: boolean } | string>;
  final_url_list?: string | string[];
  flouri_scraping_list?: boolean[];
}

// Helper function to update flouri_scraping_enabled within campaign structure
function updateCampaignScrapingPermissions(
  campaign: AppCampaignConfig | Campaign,
  permissions: Map<string, boolean>
): void {
  if (!campaign || !permissions || permissions.size === 0) return;

  const campaignType = campaign.campaign_type || '';

  // Convert Map to plain object with normalized URLs as keys
  // This ensures URLs with/without trailing slashes are matched correctly
  const permissionsObj: Record<string, boolean> = {};
  permissions.forEach((value, key) => {
    const normalizedKey = normalizeUrl(key);
    permissionsObj[normalizedKey] = value;
  });

  // Meta branch: walk ad_sets[*].ads[*].final_urls. The READ path at
  // utils/landingPages.ts:163 keys off `platform_source` alone (NOT
  // gating on ad_sets truthiness) — mirror that so a Meta campaign
  // without ad_sets can't fall through to the Google walkers below.
  // The saved-config Meta entries don't carry a top-level
  // `landing_pages` array (cf. Google), so the cross-campaign
  // normalization in saveCampaignConfiguration relies on this branch
  // to flip `flouri_scraping_enabled` on individual ad final_urls;
  // that's what extractLandingPageUrls then reads as the per-URL
  // authorization fallback when `landing_pages` is missing.
  if (campaign.platform_source === 'Meta Ads') {
    if (campaign.ad_sets) {
      campaign.ad_sets.forEach((adSet) => {
        (adSet.ads ?? []).forEach((ad) => {
          // `MetaAd` doesn't surface `final_urls` on its public API
          // type — but saved-config Meta ad entries do. Cast per-ad
          // (not per-array) so `adSet.ads` keeps its real `MetaAd[]`
          // typing and only the loop variable is treated as having
          // `final_urls`. Same per-element pattern used in the
          // PMax / non-PMax branches below.
          const adWithUrls = ad as unknown as UrlItem;
          if (adWithUrls.final_urls && Array.isArray(adWithUrls.final_urls)) {
            adWithUrls.final_urls.forEach((urlObj) => {
              if (typeof urlObj === 'object' && 'url' in urlObj) {
                const normalizedUrl = normalizeUrl(urlObj.url);
                if (Object.hasOwn(permissionsObj, normalizedUrl)) {
                  (urlObj as { url: string; flouri_scraping_enabled?: boolean }).flouri_scraping_enabled = permissionsObj[normalizedUrl];
                }
              }
            });
          }
        });
      });
    }
  } else if (campaignType.includes('PERFORMANCE_MAX')) {
    // Update asset groups for Performance Max campaigns
    if (campaign.asset_group_list) {
      campaign.asset_group_list.forEach((assetGroup) => {
        const assetGroupItem = assetGroup as unknown as UrlItem;
        // Handle new structure with final_urls array
        if (assetGroup.final_urls && Array.isArray(assetGroup.final_urls)) {
          assetGroup.final_urls.forEach((urlObj) => {
            if (typeof urlObj === 'object' && 'url' in urlObj) {
              const normalizedUrl = normalizeUrl(urlObj.url);
              if (Object.hasOwn(permissionsObj, normalizedUrl)) {
                (urlObj as { url: string; flouri_scraping_enabled?: boolean }).flouri_scraping_enabled = permissionsObj[normalizedUrl];
              }
            }
          });
        }
        // Convert old structure (final_url_list string) to new structure
        else if (assetGroup.final_url_list) {
          const urls = parseUrlField(assetGroup.final_url_list);
          assetGroup.final_urls = urls.map((url: string) => ({
            url,
            flouri_scraping_enabled: permissionsObj[normalizeUrl(url)] ?? false
          }));
          delete assetGroup.final_url_list;
          delete assetGroupItem.flouri_scraping_list;
        }
      });
    }
  } else if (campaign.ads_group_list) {
    // Update search and shopping campaigns
    campaign.ads_group_list.forEach((adGroup) => {
      // Update keywords
      if (adGroup.keywords_list) {
        adGroup.keywords_list.forEach((keyword) => {
          const keywordItem = keyword as unknown as UrlItem;
          if (keywordItem.final_urls && Array.isArray(keywordItem.final_urls)) {
            keywordItem.final_urls.forEach((urlObj) => {
              if (typeof urlObj === 'object' && 'url' in urlObj) {
                const normalizedUrl = normalizeUrl(urlObj.url);
                if (Object.hasOwn(permissionsObj, normalizedUrl)) {
                  urlObj.flouri_scraping_enabled = permissionsObj[normalizedUrl];
                }
              }
            });
          } else if (keywordItem.final_url_list) {
            const urls = parseUrlField(keywordItem.final_url_list);
            keywordItem.final_urls = urls.map((url: string) => ({
              url,
              flouri_scraping_enabled: permissionsObj[normalizeUrl(url)] ?? false
            }));
            delete keywordItem.final_url_list;
            delete keywordItem.flouri_scraping_list;
          }
        });
      }

      // Update ads
      const adsList = adGroup.ads_list || adGroup.ads;
      if (adsList) {
        adsList.forEach((ad) => {
          const adItem = ad as unknown as UrlItem;
          if (adItem.final_urls && Array.isArray(adItem.final_urls)) {
            adItem.final_urls.forEach((urlObj) => {
              if (typeof urlObj === 'object' && 'url' in urlObj) {
                const normalizedUrl = normalizeUrl(urlObj.url);
                if (Object.hasOwn(permissionsObj, normalizedUrl)) {
                  urlObj.flouri_scraping_enabled = permissionsObj[normalizedUrl];
                }
              }
            });
          } else if (adItem.final_url_list) {
            const urls = parseUrlField(adItem.final_url_list);
            adItem.final_urls = urls.map((url: string) => ({
              url,
              flouri_scraping_enabled: permissionsObj[normalizeUrl(url)] ?? false
            }));
            delete adItem.final_url_list;
            delete adItem.flouri_scraping_list;
          }
        });
      }
    });
  }
}

// Parse URL field from various formats
function parseUrlField(urlField: unknown): string[] {
  if (!urlField || urlField === '') {
    return [];
  }

  const urlStr = String(urlField).trim();

  // Try to parse as JSON array first
  if (urlStr.startsWith('[')) {
    try {
      const parsed = JSON.parse(urlStr);
      if (Array.isArray(parsed)) {
        return parsed.filter(url => url && typeof url === 'string');
      }
    } catch {
      // Not valid JSON, continue to other methods
    }
  }

  // Split by common delimiters
  if (urlStr.includes(',')) {
    return urlStr.split(',').map(url => url.trim()).filter(url => url);
  }

  if (urlStr.includes(';')) {
    return urlStr.split(';').map(url => url.trim()).filter(url => url);
  }

  // Single URL
  return [urlStr];
}

/**
 * Save campaign configuration including ad group selections and landing page permissions
 */
export async function saveCampaignConfiguration(
  campaign: Campaign,
  selectedAdGroups: Set<string>,
  selectedLandingPages?: Map<string, boolean>,
  queryClient?: QueryClient
): Promise<void> {
  const memberUuid = localStorage.getItem(SESSION_KEYS.MEMBER_UUID);
  if (!memberUuid) {
    throw new Error('Member ID not found. Please log in again.');
  }

  // Create updated campaign object with current checkbox states
  const updatedCampaign = { ...campaign };

  // Update ad groups with flouri_optimize_bids checkbox states
  if (updatedCampaign.ads_group_list) {
    updatedCampaign.ads_group_list = updatedCampaign.ads_group_list.map(adGroup => {
      const adGroupId = adGroup.ad_group_id || adGroup.ads_group_id || '';
      // Check for Manual CPC - case-insensitive to handle both 'Manual CPC' and 'MANUAL_CPC'
      if (adGroup.bidding_strategy_type?.toUpperCase().includes('MANUAL')) {
        return {
          ...adGroup,
          flouri_optimize_bids: selectedAdGroups.has(adGroupId)
        };
      }
      return adGroup;
    });
  }

  // Update landing pages with authorized checkbox states
  if (updatedCampaign.landing_pages && selectedLandingPages) {
    updatedCampaign.landing_pages = updatedCampaign.landing_pages.map(page => ({
      ...page,
      authorized: selectedLandingPages.get(page.url) ?? page.authorized ?? false
    }));
  }

  // Update flouri_scraping_enabled within campaign structure (asset groups, keywords, ads)
  if (selectedLandingPages) {
    updateCampaignScrapingPermissions(updatedCampaign, selectedLandingPages);
  }

  // Fetch current full configuration
  const currentFullConfig = await optimizationApi.loadConfiguration(memberUuid);
  const allCampaigns = currentFullConfig.configuration?.campaign_list || [];

  // Normalize landing page permissions: if any URL's permission is updated,
  // apply that change to ALL campaigns that use that URL
  if (selectedLandingPages && selectedLandingPages.size > 0) {
    console.log('[saveConfiguration] Normalizing landing page permissions across all campaigns');

    // Create a map of URLs that have been updated, with normalized URLs as keys
    // This ensures URLs with/without trailing slashes are treated as the same URL
    const updatedUrls = new Map<string, boolean>();
    selectedLandingPages.forEach((authorized, url) => {
      const normalizedUrl = normalizeUrl(url);
      updatedUrls.set(normalizedUrl, authorized);
    });

    // Update landing pages and flouri_scraping_enabled in ALL campaigns that share these URLs
    allCampaigns.forEach((campaignConfig) => {
      // Update the display landing_pages array
      if (campaignConfig.landing_pages && Array.isArray(campaignConfig.landing_pages)) {
        campaignConfig.landing_pages = campaignConfig.landing_pages.map((page) => {
          // Normalize URLs for comparison to handle trailing slash differences
          const normalizedPageUrl = normalizeUrl(page.url);

          // Check if this URL (normalized) was updated
          if (updatedUrls.has(normalizedPageUrl)) {
            const newAuthorized = updatedUrls.get(normalizedPageUrl)!;
            if (page.authorized !== newAuthorized) {
              console.log(`[saveConfiguration] Updating ${page.url} in campaign ${campaignConfig.campaign_id}: ${page.authorized} → ${newAuthorized}`);
            }
            return {
              ...page,
              authorized: newAuthorized
            };
          }
          return page;
        });
      }

      // CRITICAL: Also update flouri_scraping_enabled in the actual data structures
      // This ensures all campaigns using the same URL have the same permission
      updateCampaignScrapingPermissions(campaignConfig, updatedUrls);
    });
  }

  // Find and update the specific campaign, preserving fields from the saved config
  const campaignIndex = allCampaigns.findIndex(
    (c) => c.campaign_id === updatedCampaign.campaign_id
  );

  if (campaignIndex >= 0) {
    // Merge: keep saved config fields (budget, target_density, etc) but update ad groups and asset groups
    // IMPORTANT: Only update fields that are actually present to avoid wiping out data
    // Note: landing page permissions are stored as flouri_scraping_enabled in final_urls arrays
    const existingConfig = allCampaigns[campaignIndex];

    // Only update ads_group_list if it's present in the campaign object
    if (updatedCampaign.ads_group_list !== undefined) {
      // Convert Campaign's AdGroup[] to AppCampaignConfig's AdGroupConfig[]
      existingConfig.ads_group_list = updatedCampaign.ads_group_list.map(adGroup => ({
        ad_group_id: adGroup.ad_group_id,
        ads_group_id: adGroup.ads_group_id,
        ad_group_name: adGroup.ad_group_name,
        ads_group_name: adGroup.ads_group_name,
        status: adGroup.status,
        max_cpc_bid: adGroup.max_cpc_bid,
        bidding_strategy_type: adGroup.bidding_strategy_type,
        flouri_optimize_bids: adGroup.flouri_optimize_bids,
        keywords_list: adGroup.keywords_list?.map(k => ({
          keyword_id: k.keyword_id,
          keyword_text: k.keyword_text,
          match_type: k.match_type,
          max_cpc_bid: k.max_cpc_bid,
          status: k.status,
          quality_score: k.quality_score,
          final_urls: k.final_urls
        })),
        ads: adGroup.ads?.map(ad => ({
          ad_id: ad.ad_id,
          ad_type: ad.ad_type,
          ad_name: ad.ad_name,
          status: ad.status,
          headlines: ad.headlines?.map(h => typeof h === 'string' ? h : h.text),
          descriptions: ad.descriptions?.map(d => typeof d === 'string' ? d : d.text),
          final_urls: ad.final_urls,
          path1: ad.path1,
          path2: ad.path2,
          rda_headlines: ad.rda_headlines,
          rda_descriptions: ad.rda_descriptions,
          rda_long_headline: ad.rda_long_headline ?? ad.long_headline,
          rda_business_name: ad.rda_business_name ?? ad.business_name,
          rda_marketing_images: ad.rda_marketing_images,
          rda_square_marketing_images: ad.rda_square_marketing_images,
          rda_logo_images: ad.rda_logo_images
        })),
        ads_list: adGroup.ads_list?.map(ad => ({
          ad_id: ad.ad_id,
          ad_type: ad.ad_type,
          ad_name: ad.ad_name,
          status: ad.status,
          headlines: ad.headlines?.map(h => typeof h === 'string' ? h : h.text),
          descriptions: ad.descriptions?.map(d => typeof d === 'string' ? d : d.text),
          final_urls: ad.final_urls,
          path1: ad.path1,
          path2: ad.path2,
          rda_headlines: ad.rda_headlines,
          rda_descriptions: ad.rda_descriptions,
          rda_long_headline: ad.rda_long_headline ?? ad.long_headline,
          rda_business_name: ad.rda_business_name ?? ad.business_name,
          rda_marketing_images: ad.rda_marketing_images,
          rda_square_marketing_images: ad.rda_square_marketing_images,
          rda_logo_images: ad.rda_logo_images
        }))
      }));
    }

    // Only update asset_group_list if it's present in the campaign object
    if (updatedCampaign.asset_group_list !== undefined) {
      existingConfig.asset_group_list = updatedCampaign.asset_group_list.map(ag => ({
        asset_group_id: ag.asset_group_id,
        asset_group_name: ag.asset_group_name,
        asset_group_status: ag.asset_group_status,
        status: ag.status,
        final_urls: ag.final_urls,
        final_url_list: ag.final_url_list,
        asset_list: ag.asset_list?.map(a => ({
          asset_id: a.asset_id,
          type: a.type,
          asset_type: a.asset_type,
          text: a.text,
          content: a.content,
          asset_text: a.asset_text,
          performance_label: a.performance_label,
          status: a.status
        }))
      }));
    }
  } else {
    // New campaign - need to ensure required fields
    const newCampaignConfig: AppCampaignConfig = {
      campaign_id: updatedCampaign.campaign_id,
      budget: updatedCampaign.budget,
      target_density: currentFullConfig.configuration.target_density || 0.5,
      flouri_optimize_budget: false,
      google_ads_account_id: updatedCampaign.google_ads_account_id,
      campaign_type: updatedCampaign.campaign_type,
      landing_pages: updatedCampaign.landing_pages?.map(lp => ({
        url: lp.url,
        hash: lp.hash,
        authorized: lp.authorized,
        source_name: lp.source_name,
        source_type: lp.source_type,
        source_id: lp.source_id
      }))
    };

    // Add ads_group_list if present
    if (updatedCampaign.ads_group_list) {
      newCampaignConfig.ads_group_list = updatedCampaign.ads_group_list.map(adGroup => ({
        ad_group_id: adGroup.ad_group_id,
        ads_group_id: adGroup.ads_group_id,
        ad_group_name: adGroup.ad_group_name,
        ads_group_name: adGroup.ads_group_name,
        status: adGroup.status,
        max_cpc_bid: adGroup.max_cpc_bid,
        bidding_strategy_type: adGroup.bidding_strategy_type,
        flouri_optimize_bids: adGroup.flouri_optimize_bids,
        keywords_list: adGroup.keywords_list?.map(k => ({
          keyword_id: k.keyword_id,
          keyword_text: k.keyword_text,
          match_type: k.match_type,
          max_cpc_bid: k.max_cpc_bid,
          status: k.status,
          quality_score: k.quality_score,
          final_urls: k.final_urls
        })),
        ads: adGroup.ads?.map(ad => ({
          ad_id: ad.ad_id,
          ad_type: ad.ad_type,
          ad_name: ad.ad_name,
          status: ad.status,
          headlines: ad.headlines?.map(h => typeof h === 'string' ? h : h.text),
          descriptions: ad.descriptions?.map(d => typeof d === 'string' ? d : d.text),
          final_urls: ad.final_urls,
          path1: ad.path1,
          path2: ad.path2,
          rda_headlines: ad.rda_headlines,
          rda_descriptions: ad.rda_descriptions,
          rda_long_headline: ad.rda_long_headline ?? ad.long_headline,
          rda_business_name: ad.rda_business_name ?? ad.business_name,
          rda_marketing_images: ad.rda_marketing_images,
          rda_square_marketing_images: ad.rda_square_marketing_images,
          rda_logo_images: ad.rda_logo_images
        })),
        ads_list: adGroup.ads_list?.map(ad => ({
          ad_id: ad.ad_id,
          ad_type: ad.ad_type,
          ad_name: ad.ad_name,
          status: ad.status,
          headlines: ad.headlines?.map(h => typeof h === 'string' ? h : h.text),
          descriptions: ad.descriptions?.map(d => typeof d === 'string' ? d : d.text),
          final_urls: ad.final_urls,
          path1: ad.path1,
          path2: ad.path2,
          rda_headlines: ad.rda_headlines,
          rda_descriptions: ad.rda_descriptions,
          rda_long_headline: ad.rda_long_headline ?? ad.long_headline,
          rda_business_name: ad.rda_business_name ?? ad.business_name,
          rda_marketing_images: ad.rda_marketing_images,
          rda_square_marketing_images: ad.rda_square_marketing_images,
          rda_logo_images: ad.rda_logo_images
        }))
      }));
    }

    // Add asset_group_list if present
    if (updatedCampaign.asset_group_list) {
      newCampaignConfig.asset_group_list = updatedCampaign.asset_group_list.map(ag => ({
        asset_group_id: ag.asset_group_id,
        asset_group_name: ag.asset_group_name,
        asset_group_status: ag.asset_group_status,
        status: ag.status,
        final_urls: ag.final_urls,
        final_url_list: ag.final_url_list,
        asset_list: ag.asset_list?.map(a => ({
          asset_id: a.asset_id,
          type: a.type,
          asset_type: a.asset_type,
          text: a.text,
          content: a.content,
          asset_text: a.asset_text,
          performance_label: a.performance_label,
          status: a.status
        }))
      }));
    }

    allCampaigns.push(newCampaignConfig);
  }

  // Save via PUT to /app-configuration/update using apiClient for proper interceptors
  await apiClient.put('/app-configuration/update', {
    configuration: {
      ...currentFullConfig.configuration,
      campaign_list: allCampaigns
    }
  });

  // Optimistically update the query cache immediately for instant UI feedback
  if (queryClient) {
    // Update app-config cache with the new configuration
    const appConfigKeys = queryClient.getQueriesData({ queryKey: ['app-config'] });
    appConfigKeys.forEach(([queryKey, oldData]) => {
      if (oldData && typeof oldData === 'object' && 'configuration' in oldData) {
        queryClient.setQueryData(queryKey, {
          ...oldData,
          configuration: {
            ...(oldData.configuration as Record<string, unknown>),
            campaign_list: allCampaigns
          }
        });
      }
    });

    // Update campaign-from-cache for the specific campaign
    const campaignCacheKeys = queryClient.getQueriesData({
      queryKey: ['campaign-from-cache', updatedCampaign.campaign_id]
    });
    campaignCacheKeys.forEach(([queryKey, oldCampaign]) => {
      if (oldCampaign && typeof oldCampaign === 'object') {
        queryClient.setQueryData(queryKey, {
          ...oldCampaign,
          ads_group_list: updatedCampaign.ads_group_list,
          asset_group_list: updatedCampaign.asset_group_list,
        });
      }
    });

    console.log('[saveConfiguration] Optimistically updated cache with new asset/ad groups');

    // IMPORTANT: Do NOT invalidate campaign-from-cache after optimistic update
    // The invalidation triggers a refetch that uses stale useMemo data, overwriting
    // the optimistic update and causing the checkbox to "vanish"
    // Only invalidate app-config for background sync of other data
    await queryClient.invalidateQueries({ queryKey: ['app-config'] });
    console.log('[saveConfiguration] Invalidated app-config for background sync (campaign-from-cache preserved)');
  }
}
